import { create } from "zustand";
import { persist } from "zustand/middleware";

import { queryClient } from "@/services/query-client";
import type { TokenPair } from "@/meta/types";

const STORAGE_KEY = "mrm.session";

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  signIn: (response: TokenPair) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  signOut: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      signIn: (response) =>
        set({
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
        }),
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      signOut: () => {
        // Everything cached was fetched as the person leaving: their profile,
        // their permissions, their policy. The shell reads those through
        // `ensureQueryData`, which serves what it has while it is fresh — a
        // minute for the profile, five for the policy — so without this the
        // next person to sign in on this tab wears the last one's answers.
        queryClient.clear();
        set({ accessToken: null, refreshToken: null });
      },
    }),
    { name: STORAGE_KEY },
  ),
);

/** Read outside React — the axios interceptors need the current tokens. */
export const authSnapshot = () => useAuthStore.getState();
